import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  XCircle,
  SkipForward,
  Ban,
  Download,
  GraduationCap,
} from 'lucide-react';
import {
  bulkImportProfessors,
  fetchActiveCampuses,
  type BulkImportProfessorsResponse,
  type BulkImportProfessorsOptions,
  type CampusOption,
} from '@/services/userService';

interface BulkImportProfessorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'upload' | 'options' | 'importing' | 'results';

// Template headers matching ministry nomenclature for professors
const TEMPLATE_HEADERS = [
  'N° CNIE',
  'N° passeport',
  'Nom',
  'Prénom',
  'Nom en arabe',
  'Prenom en arabe',
  'Email',
  'Sexe',
  'Date de naissance',
  'Nationalité',
  'Status',
  'Taux horaire',
  'Salaire mensuel',
  'Sigle université',
  'Université',
  'Sigle établissement',
  'Etablissement',
  'Département d\'attache',
  'Etablissement public',
  'Etablissement/Organisme d\'origine du vacataire',
  'Grade',
  'Diplôme universitaire',
  'Spécialité',
  'Date de recrutement',
  'Matières enseignées',
  'Handicapé',
  'Session',
  'Statut',
  'Motif de la décision',
];

// Example row for the template (blank - user fills their own data)
const TEMPLATE_EXAMPLE_ROW = [
  '', // N° CNIE
  '', // N° passeport
  '', // Nom
  '', // Prénom
  '', // Nom en arabe
  '', // Prenom en arabe
  '', // Email
  '', // Sexe (m/f)
  '', // Date de naissance (DD/MM/YYYY)
  '', // Nationalité
  '', // Status (v=vacataire, p=permanent)
  '', // Taux horaire
  '', // Salaire mensuel
  '', // Sigle université
  '', // Université
  '', // Sigle établissement
  '', // Etablissement
  '', // Département d'attache
  '', // Etablissement public (o/n)
  '', // Etablissement d'origine du vacataire
  '', // Grade
  '', // Diplôme universitaire
  '', // Spécialité
  '', // Date de recrutement (DD/MM/YYYY)
  '', // Matières enseignées
  '', // Handicapé (n/o)
  '', // Session
  '', // Statut
  '', // Motif de la décision
];

export const BulkImportProfessorsModal: React.FC<BulkImportProfessorsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkImportProfessorsResponse | null>(null);

  // Options
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [loadingCampuses, setLoadingCampuses] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load campuses when moving to options step
  const loadCampuses = useCallback(async () => {
    setLoadingCampuses(true);
    try {
      const response = await fetchActiveCampuses();
      setCampuses(response.campuses);
      // Auto-select if only one campus
      if (response.campuses.length === 1) {
        setSelectedCampusIds([response.campuses[0].id]);
      }
    } catch (err) {
      console.error('Failed to load campuses:', err);
    } finally {
      setLoadingCampuses(false);
    }
  }, []);

  // Download Excel template
  const handleDownloadTemplate = useCallback(() => {
    // Create CSV content (Excel-compatible)
    const csvContent = [
      TEMPLATE_HEADERS.join(';'),
      TEMPLATE_EXAMPLE_ROW.join(';'),
    ].join('\n');

    // Create blob with BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_import_enseignants.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleFileSelect = (selectedFile: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext =>
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
      setError('Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setStep('options');
    loadCampuses();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Toggle campus selection
  const handleCampusToggle = (campusId: string) => {
    setSelectedCampusIds(prev => {
      if (prev.includes(campusId)) {
        return prev.filter(id => id !== campusId);
      } else {
        return [...prev, campusId];
      }
    });
  };

  const handleImport = async () => {
    if (!file) return;

    setStep('importing');
    setError(null);

    try {
      const options: BulkImportProfessorsOptions = {};

      if (selectedCampusIds.length > 0) {
        options.campusIds = selectedCampusIds;
      }

      const response = await bulkImportProfessors(file, options);

      // Set the results and transition to results step
      setResults(response);
      setStep('results');
    } catch (err) {
      console.error('[BulkImportProfessors] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
      setStep('options');
    }
  };

  const handleClose = () => {
    // If there were successful imports, notify parent to refresh the list
    const hadSuccessfulImports = results && results.successCount > 0;

    // Reset state
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
    setSelectedCampusIds([]);

    // Call onSuccess AFTER resetting state but BEFORE onClose
    if (hadSuccessfulImports) {
      onSuccess();
    }
    onClose();
  };

  const handleStartOver = () => {
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 z-10">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-full">
              <GraduationCap size={24} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Import en masse des enseignants
              </h2>
              <p className="text-sm text-gray-500">
                Importez plusieurs professeurs depuis un fichier Excel
              </p>
            </div>
          </div>

          {/* Step: Upload */}
          {step === 'upload' && (
            <div>
              {/* Drop zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragActive
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <Upload
                  size={48}
                  className={`mx-auto mb-4 ${
                    dragActive ? 'text-purple-500' : 'text-gray-400'
                  }`}
                />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Glissez-déposez votre fichier Excel ici
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  ou cliquez pour sélectionner un fichier
                </p>
                <p className="text-xs text-gray-400">
                  Formats acceptés : .xlsx, .xls, .csv (max 10 Mo)
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Help section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Format attendu</h4>
                  <button
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <Download size={16} />
                    Télécharger le modèle
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Le fichier Excel doit contenir les colonnes suivantes :
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><span className="font-medium text-red-600">* Nom</span> - Nom de famille (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Prénom</span> - Prénom (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Email</span> - Adresse email (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Taux horaire / Salaire</span> - Un des deux requis</li>
                  <li><span className="font-medium">N° CNIE</span> - Carte nationale d'identité</li>
                  <li><span className="font-medium">Status</span> - v (vacataire) ou p (permanent)</li>
                  <li><span className="font-medium">Date de naissance</span> - Format DD/MM/YYYY</li>
                  <li><span className="font-medium">Etablissement public</span> - o ou n</li>
                  <li className="text-gray-400">+ autres champs optionnels...</li>
                </ul>
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note :</strong> Pour les professeurs permanents, remplir "Salaire mensuel" et laisser "Taux horaire" vide.
                    Pour les vacataires, remplir "Taux horaire" et laisser "Salaire mensuel" vide.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step: Options */}
          {step === 'options' && file && (
            <div>
              {/* Selected file */}
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <FileSpreadsheet size={24} className="text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">{file.name}</p>
                  <p className="text-sm text-green-700">
                    {(file.size / 1024).toFixed(1)} Ko
                  </p>
                </div>
                <button
                  onClick={handleStartOver}
                  className="text-green-600 hover:text-green-800"
                >
                  Changer
                </button>
              </div>

              {/* Options */}
              <div className="space-y-4 mb-6">
                {/* Campus selection */}
                {loadingCampuses ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Chargement des campus...</span>
                  </div>
                ) : campuses.length > 1 ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campus assigné(s) aux professeurs
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Les professeurs peuvent être assignés à plusieurs campus
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-lg">
                      {campuses.map((campus) => (
                        <label
                          key={campus.id}
                          className="flex items-center gap-3 cursor-pointer p-2 hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCampusIds.includes(campus.id)}
                            onChange={() => handleCampusToggle(campus.id)}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-gray-900">
                            {campus.name} - {campus.city}
                            {campus.isMainCampus && (
                              <span className="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                                Principal
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : campuses.length === 1 ? (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>Campus :</strong> {campuses[0].name} - {campuses[0].city}
                    </p>
                  </div>
                ) : null}
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleImport}
                  disabled={campuses.length > 1 && selectedCampusIds.length === 0}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Upload size={18} />
                  Importer
                </button>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="py-12 text-center">
              <Loader2 size={48} className="mx-auto mb-4 text-purple-600 animate-spin" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Import en cours...
              </p>
              <p className="text-sm text-gray-500">
                Veuillez patienter, cela peut prendre quelques minutes
              </p>
            </div>
          )}

          {/* Step: Results */}
          {step === 'results' && results && (
            <div>
              {/* Success banner */}
              {results.successCount > 0 && (
                <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">
                        Import terminé avec succès !
                      </p>
                      <p className="text-sm text-green-700">
                        {results.successCount} professeur{results.successCount > 1 ? 's' : ''} importé{results.successCount > 1 ? 's' : ''}.
                        Les emails d'activation ont été envoyés.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Success */}
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle size={24} className="mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{results.successCount}</p>
                  <p className="text-sm text-green-600">Importés</p>
                </div>

                {/* Skipped */}
                <div className="p-4 bg-yellow-50 rounded-lg text-center">
                  <SkipForward size={24} className="mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold text-yellow-700">{results.skippedCount}</p>
                  <p className="text-sm text-yellow-600">Ignorés (doublons)</p>
                </div>

                {/* Errors */}
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <XCircle size={24} className="mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-700">{results.errorCount}</p>
                  <p className="text-sm text-red-600">Erreurs</p>
                </div>
              </div>

              {/* Skipped duplicates list */}
              {results.skippedDuplicates.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Doublons ignorés ({results.skippedDuplicates.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 rounded-lg p-3 text-sm">
                    {results.skippedDuplicates.map((dup, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-yellow-100 last:border-0">
                        <span className="text-yellow-900">
                          Ligne {dup.rowNumber}: {dup.prenom} {dup.nom}
                        </span>
                        <span className="text-yellow-700">{dup.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejected (missing required fields) list */}
              {results.rejectedMissingRequired.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <Ban size={16} />
                    Rejetés (champs requis manquants) ({results.rejectedMissingRequired.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.rejectedMissingRequired.map((rej, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                        <span className="text-red-900">
                          Ligne {rej.rowNumber}: {rej.prenom} {rej.nom}
                        </span>
                        <span className="text-red-700">{rej.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error list */}
              {results.results.filter(r => !r.success && r.error).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Erreurs d'import
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.results
                      .filter(r => !r.success && r.error)
                      .map((res, idx) => (
                        <div key={idx} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                          <span className="text-red-900">
                            Ligne {res.rowNumber}: {res.prenom} {res.nom}
                          </span>
                          <span className="text-red-700">{res.error}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Importer un autre fichier
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportProfessorsModal;
